import { createFileRoute, Outlet, useMatches } from "@tanstack/react-router";
import { leafCanonicalLink, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { awningBeforeAfterScripts } from "@/lib/awningImageJsonLd";

import { ServicePage } from "@/components/ServicePage";
import hero from "@/assets/original/stock-commercial.jpg";
import heroPicture from "@/assets/original/stock-commercial.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
// Page-specific share image: a real Texas Made Shade awning install (Tuff Shed
// storefront) so social previews show our work, not a generic stock photo.
import shareImage from "@/assets/tuff-shed-awning.jpg";
import blueCanvas from "@/assets/awning-blue-canvas.jpg";
import mainStreetAwning from "@/assets/awning-400-s-main.jpg";
import stripedFabric from "@/assets/awning-striped-fabric.jpg";
import blueStripedFabric from "@/assets/awning-blue-striped-fabric.jpg";
import bradzoilBanner from "@/assets/awning-bradzoil-banner.jpg";
import tuffShedAwning from "@/assets/tuff-shed-awning.jpg";
import steelStitchNewCover from "@/assets/awning-steel-stitch-walkway-new-cover.jpg.asset.json";
import steelStitchBefore from "@/assets/awning-steel-stitch-walkway-before.jpg.asset.json";




export const Route = createFileRoute("/awnings")({
  head: (ctx) => ({
      // Self-canonical: /awnings is its own leaf page (children at
      // /awnings/in/$city are separate routes with their own canonicals).
      meta: routeMeta(ctx, {
        title: "Custom Awnings San Antonio & Austin TX | Texas Made Shade",
        description: "Awnings in San Antonio, awnings in New Braunfels and Commercial Awnings Austin — welded aluminum frames + Sunbrella® canvas.",
        image: shareImage,
        imageAlt: "Custom welded aluminum storefront awning by Texas Made Shade",
        socialTitle: "Awnings in San Antonio, New Braunfels & Austin — Texas Made Shade",
        socialDescription: "Welded aluminum frames + Sunbrella® canvas. Awnings in San Antonio, New Braunfels and Commercial Awnings Austin. Built in Seguin, TX.",
        keywords: "Awning Company San Antonio, awning company San Antonio TX, awning companies San Antonio, San Antonio awning company, Awnings Austin TX, awnings Austin, awning company Austin, Sail Shades San Antonio, shade sails San Antonio, Awnings in San Antonio, Awnings in New Braunfels, Commercial Awnings Austin, awnings Texas, Texas awning company, custom awnings, metal awnings, canvas awnings, retractable awnings, residential awnings, commercial awnings",
      }),
    links: leafCanonicalLink(ctx, "/awnings"),
    scripts: [
      ...serviceHeadScripts({
        path: "/awnings",
        serviceName: "Custom Awnings",
        breadcrumbName: "Awnings",
        description: "Custom welded aluminum awnings with Sunbrella® & Tempotest® canvas — residential and commercial. Patterned.",
      }),
      // Before/after re-cover photos as indexable ImageObjects.
      ...awningBeforeAfterScripts({
        path: "/awnings",
        beforeUrl: steelStitchBefore.url,
        afterUrl: steelStitchNewCover.url,
      }),
    ],
    }),

  component: ServiceRoute,
});

function ServiceRoute() {
  const matches = useMatches();
  const leaf = matches[matches.length - 1];
  if (leaf?.routeId !== Route.id) return <Outlet />;
  return (
    <ServicePage
      currentPath="/awnings"
      serviceName="Awnings"
      keywordOutline={[
        { id: "overview", label: "Custom Awnings — Residential & Commercial in Texas" },
        { id: "why-us", label: "Why Choose Texas Made Shade for Awnings" },
        { id: "details", label: "Awning Frames, Canvas & Build Details" },
        { id: "applications", label: "Where We Install Custom Awnings" },
        { id: "faqs", label: "Custom Awning FAQs" },
      ]}
      eyebrow="★ AWNINGS ★"
      title="Custom Awnings — Residential & Commercial"
      intro="Custom awnings in San Antonio, New Braunfels and commercial awnings Austin — welded aluminum frames with premium Sunbrella® & Tempotest® canvas. The Awning Company San Antonio and Austin businesses choose for custom shade, patterned, fabricated and installed by the same Texas crew that built it, from our Seguin shop."
      heroImage={heroPicture}
      letsTalkImageSrc={steelStitchNewCover.url}
      letsTalkImageAlt="Steel stitch walkway awning with a brand-new burgundy canvas cover at Jourdanton ISD School, custom built and installed by Texas Made Shade in Texas"
      letsTalkAwningFields

      heroCta={{
        eyebrow: "Free, no-pressure estimate",
        headline: "Get a Free Estimate on Your Custom Awning",
        subhead: "Same-business-day response. Send a few photos and rough measurements — we'll spec it and quote it. Built in Seguin, installed across Texas.",
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Custom Awning")}`,
        primaryLabel: "Get a Free Estimate",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "Custom welded aluminum frames — no prefab kits",
        "Sunbrella® & Tempotest® premium canvas",
        "On-site measurement and installation included",
        "Storefront, patio, window and door awnings",
        "Engineered for Texas wind and 100°+ heat",
        "Re-cover service for existing frames",
      ]}
      body={[
        { heading: "Storefront & Commercial Awnings", text: "Branded storefront awnings, restaurant patio canopies, and commercial entry covers — built for daily use and Texas weather." },
        { heading: "Residential Awnings", text: "Window awnings, patio covers, door awnings, and porch shade — designed to match your home and HOA." },
        { heading: "Gazebo-Pergola Covers & Patio Shade", text: "Looking to shade an open pergola, patio or outdoor kitchen? We build custom pergola covers with welded aluminum frames and Sunbrella® canvas tops across Texas.", cta: { label: "See pergola covers in Texas", href: "/pergola-covers" } },
        { heading: "Re-Covers & Repair", text: "Recover an existing frame with new Sunbrella® canvas — same quality, fraction of a full replacement." },
      ]}
      applications={["Storefronts", "Restaurants", "Patios", "Porches", "Windows & Doors", "Commercial Entries"]}
      faqs={[
        { q: "How long do custom awnings last in San Antonio, Austin & Texas heat?", a: "Our welded aluminum awning frames are built to last 20+ years. Sunbrella® and Tempotest® canvas tops typically hold their color and integrity for 8–12 years in direct Texas sun before needing a re-cover, depending on exposure in San Antonio, Austin and the Hill Country." },
        { q: "Do you build commercial storefront awnings in San Antonio, Austin and New Braunfels?", a: "Yes — commercial storefront awnings, restaurant patio canopies and branded entry awnings in San Antonio, Austin, New Braunfels and across Central Texas are a core part of our business. We pattern on-site, fabricate the welded aluminum frame in our Seguin shop, sew the canvas in-house, and install with our own crew." },
        { q: "Can you re-cover an existing awning frame with new Sunbrella® canvas?", a: "Yes — if your existing aluminum or steel frame is structurally sound, we re-cover it with new Sunbrella® canvas at a fraction of a full replacement. Re-covers are available across San Antonio, Austin, New Braunfels and the Hill Country." },
        { q: "What Texas cities do you install custom awnings in?", a: "We're based in Seguin and install custom awnings across San Antonio, Austin, New Braunfels, San Marcos, Boerne, Kerrville, Fredericksburg, Corpus Christi and the rest of Central & South Texas." },
        { q: "How do I get a free custom awning quote in Texas?", a: "Send measurements, photos, and project notes through the form on our homepage, or call +1 (830) 390-9588. On-site awning measurements are free across our San Antonio, Austin and Hill Country service area." },
      ]}
      gallerySection={{
        eyebrow: "In The Shop · Built In Seguin, TX",
        title: "Custom Awning Canvas & Storefront Builds — Recent Work",
        intro: "A look inside our Seguin fabrication shop and recent installs across South Texas. Every awning starts as a roll of premium Sunbrella® or Tempotest® canvas, gets patterned and sewn on our industrial line, then installed over a TIG-welded aluminum frame built by the same crew.",
        categories: ["Storefront", "Commercial", "Canvas & Fabric"],
        images: [
          {
            src: steelStitchBefore.url,
            alt: "Before photo of a faded red steel stitch walkway awning cover at Jourdanton ISD School, scheduled for replacement by Texas Made Shade",
            label: "Before · Steel Stitch Walkway Awning — Jourdanton ISD School",
            caption: "Jourdanton ISD School — sun-faded steel stitch walkway cover we're currently replacing. The tube frame is still solid, so we re-cover it with new marine-grade canvas instead of a full teardown.",
            category: "Commercial",
          },
          {
            src: steelStitchNewCover.url,
            alt: "After photo of the Jourdanton ISD School steel stitch walkway awning with a brand-new burgundy marine-grade canvas cover fabricated and installed by Texas Made Shade",
            label: "After · New Burgundy Canvas Cover — Jourdanton ISD School",
            caption: "After: the same Jourdanton ISD School walkway awning with a brand-new burgundy marine-grade canvas top, patterned and sewn in our Seguin shop and installed over the existing steel stitch frame.",
            category: "Commercial",
          },

          { src: blueCanvas, alt: "Roll of marine-grade blue Sunbrella® awning canvas staged on the cutting table inside the Texas Made Shade Seguin, TX fabrication shop", label: "Sunbrella® Canvas · Cutting Table", category: "Canvas & Fabric" },

          { src: mainStreetAwning, alt: "Custom welded aluminum entry awning with Sunbrella® canvas installed by Texas Made Shade at 400 S. Main Street storefront in Seguin, Texas", label: "400 S. Main · Seguin Storefront Awning", category: "Storefront" },
          {
            src: stripedFabric,
            alt: "Black and white striped Sunbrella® awning fabric rolled out and patterned for a custom storefront awning build at the Texas Made Shade Seguin shop",
            label: "Striped Sunbrella® · Pattern Layout",
            caption: "Classic black-and-white striped Sunbrella® canvas rolled out and patterned on the shop floor — a timeless storefront and bistro look that resists fading and mildew through years of Texas sun.",
            category: "Canvas & Fabric",
          },
          {
            src: blueStripedFabric,
            alt: "Marine-blue striped Sunbrella® awning fabric rolls staged for a coastal storefront awning build at the Texas Made Shade Seguin, TX shop",
            label: "Lots of Fabrics Available",
            caption: "Marine-blue striped Sunbrella® ready to be cut to pattern — perfect for coastal storefronts, restaurant patios, and brand-matched commercial awnings.",
            category: "Canvas & Fabric",
          },
          { src: bradzoilBanner, alt: "Hand-painted custom commercial banner awning fabricated and installed by Texas Made Shade for a Bradzoil 10 Minute Oil Change location in South Texas", label: "Bradzoil · Custom Branded Awning", category: "Commercial" },
          { src: tuffShedAwning, alt: "Welded aluminum storefront awning with Sunbrella® canvas top and hand-painted logo installed at a Tuff Shed retail location by Texas Made Shade", label: "Tuff Shed · Storefront Install", category: "Storefront" },
          
        ],
        columns: 4,
        cta: {
          headline: "Get a Free Estimate on Your Custom Awning",
          subhead: "Storefront, patio, window, or commercial entry — send a few photos and rough measurements and we'll come back same business day with sizing questions and a free, no-pressure estimate. Built in Seguin, installed across Texas.",
          buttonLabel: "Get My Free Estimate",
          href: `/#lets-talk?type=${encodeURIComponent("Custom Awning")}&notes=${encodeURIComponent("Custom awning inquiry — please describe the location (storefront, patio, window, etc.), approximate width and projection, fabric color preference, and timeline.")}`,
          phoneNumber: "+1 (830) 390-9588",
        },
      }}
      testimonials={{
        eyebrow: "Awning Customer Reviews · 5.0 Rating",
        title: "What Awning Customers Say",
        items: [
          { quote: "Welded aluminum frame and Sunbrella® canvas on our patio — survived two Texas summers without fading. Worth every penny.", author: "Lisa P.", location: "Seguin, TX", role: "Patio Awning", rating: 5 },
          
          { quote: "Fast quote, fair price, and the install crew was professional. Couldn't be happier with our new awning.", author: "David H.", location: "New Braunfels, TX", role: "Storefront Awning", rating: 5 },
          { quote: "Recovered our 12-year-old awning frame with new Sunbrella® — looks brand new at a fraction of the cost of replacement.", author: "Robert M.", location: "Austin, TX", role: "Awning Re-Cover", rating: 5 },
          { quote: "They built and installed a hand-painted commercial banner awning for our shop. Crisp logo, tight install, and zero hassle.", author: "Brad Z.", location: "South Texas", role: "Commercial Branded Awning", rating: 5 },
          { quote: "Window awnings on the front of the house turned out beautiful and HOA-approved. Same crew measured and installed.", author: "Karen S.", location: "Boerne, TX", role: "Window Awnings", rating: 5 },
        ],
      }}
    >
    </ServicePage>
  );
}

